import { MicOff } from 'lucide-react';

interface ParticipantNameplateProps {
  name: string;
  role: string;
  /** Render a muted-mic indicator alongside the name. */
  isMuted?: boolean;
  /** Smaller typography + tighter padding for PIP tiles. */
  size?: 'md' | 'sm';
}

/**
 * Two-line identity overlay rendered at the bottom-left of a participant tile.
 * Replaces LiveKit's default `.lk-participant-metadata` (hidden globally in
 * `globals.css`) with brand-consistent typography — Roobert body for the name,
 * tracking-label small caps for the role.
 */
export function ParticipantNameplate({
  name,
  role,
  isMuted = false,
  size = 'md',
}: ParticipantNameplateProps) {
  const isSm = size === 'sm';
  // md (full grid tile): the CallControlBar floats at `bottom-6` + ~54px
  // pill height (~78px reach from the screen edge). Pin the nameplate
  // just above that at `bottom-20` (80px) so it never sits behind the
  // bar but stays close enough that it doesn't visually float on a
  // full-screen tile. sm (PIP) is unaffected — the PIP lives top-right,
  // nowhere near the control bar.
  const wrapClass = isSm
    ? 'bottom-1.5 left-1.5 gap-0 rounded-md px-2 py-1'
    : 'bottom-20 left-3 gap-0.5 rounded-lg px-2.5 py-1.5';
  const nameClass = isSm ? 'text-[11px] leading-[14px]' : 'text-[13px] leading-[18px]';
  const roleClass = isSm
    ? 'text-[9px] leading-[10px] tracking-label'
    : 'text-[10px] leading-[12px] tracking-label';

  return (
    <div
      className={`pointer-events-none absolute z-10 flex flex-col bg-black/55 text-white backdrop-blur-md ${wrapClass}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`font-medium text-white ${nameClass}`}>{name}</span>
        {isMuted ? (
          <MicOff
            className={isSm ? 'h-2.5 w-2.5 text-white/70' : 'h-3 w-3 text-white/70'}
            aria-hidden="true"
            strokeWidth={2.25}
          />
        ) : null}
      </div>
      {role !== name ? (
        <span className={`text-white/55 uppercase ${roleClass}`}>{role}</span>
      ) : null}
    </div>
  );
}
